/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { DisplayObject, Point, Rectangle, RenderTexture } from 'pixi.js-legacy';
import { Camera } from '@appCanvasCommon/core/Camera';
import { convertCanvasToBlobAndDownload } from '@appCanvasCommon/export/ExportHelpers';
import { take } from 'rxjs/operators';
/**
 * Represents a captured tile containing both the rendered canvas data and its position information.
 */
export interface ICapturedTile {
	canvas: HTMLCanvasElement;
	position: ITilePosition;
	index: number;
}
/**
 * Defines the position and dimensions of a tile in the export grid.
 */
export interface ITilePosition {
	x: number;
	y: number;
	width: number;
	height: number;
}

/**
 * Offset configuration for tile positioning.
 */
export interface ITileOffset {
	x: number;
	y: number;
}
/**
 * ExportCapture class handles the capture of canvas areas for export
 * The capture process works by positioning the camera at specific draw points, rendering the current view, and
 * extracting the rendered content as canvas data. This enables the export of plans that are larger than browser
 * canvas size limits by breaking them into manageable tiles.
 * @example
 * ```typescript
 * const exporter = new ExportCapture(planApplication);
 * // Capture a specific area of the plan
 * const tile = exporter.capture({
 *   x: 0, y: 0, width: 1024, height: 768
 * });
 * // Clear captured tiles when done
 * exporter.purge();
 * ```
 */
export class ExportCapture {
	/**
	 * Creates an empty ICapturedTile object with default values.
	 * Canvas is a 1x1 transparent canvas, position values are all set to 1, and index is 0.
	 * @returns An empty ICapturedTile object with default values
	 */
	public static createEmptyTile(): ICapturedTile {
		const canvas: HTMLCanvasElement = document.createElement('canvas');
		canvas.width = 1;
		canvas.height = 1;

		const context: CanvasRenderingContext2D = canvas.getContext('2d') as CanvasRenderingContext2D;
		if (context) {
			context.clearRect(0, 0, 1, 1);
		}

		return {
			canvas: canvas,
			position: {
				x: 0,
				y: 0,
				width: 1,
				height: 1,
			},
			index: 0,
		};
	}
	/**
	 * Public accessor for reading all of the captured tiles
	 */
	public get tiles(): ICapturedTile[] {
		return this._tiles;
	}

	/**
	 * Get the current width of all tiles
	 */
	public get width(): number {
		return this._expectedWidth;
	}

	/**
	 * Get the current height of all tiles
	 */
	public get height(): number {
		return this._expectedHeight;
	}
	/**
	 * Internal storage for all captured tiles. This array maintains the sequence
	 * of captures and can be used for stitching tiles together later.
	 * @private
	 */
	private _tiles: ICapturedTile[] = [];
	/**
	/**
	 * The origin point, which is always the first tile captured. Every subsequent tile will be positioned based off of this origin
	 * @private
	 */
	private _origin: Point = new Point(0, 0);
	/**
	 * An ongoing tracker of the width for the current tiles
	 * @private
	 */
	private _expectedWidth: number = 0;
	/**
	 * An ongoing tracker of the height for the current tiles.
	 * @private
	 */
	private _expectedHeight: number = 0;
	/**
	 * Initialize the export capture system with a reference to the plan application.
	 * @param application - The AbstractApplication instance containing the plan to export
	 */
	constructor(protected application: AbstractApplication) {}
	/**
	 * Capture a specific tile area of the plan by positioning the camera and rendering.
	 * @param tileArea - The area specification (position and dimensions) to capture
	 * @param customRoot - Optional custom root DisplayObject to render (defaults to application.stage)
	 * @returns The captured tile containing canvas data and position information
	 */
	public capture(
		area: Rectangle,
		customRoot: DisplayObject = this.application.stage,
		offset?: ITileOffset
	): ICapturedTile {
		const tile: ICapturedTile = this.captureTile(area, customRoot, offset);
		this._tiles.push(tile);
		return tile;
	}
	/**
	 * Clear all captured tiles from internal storage. This should be called after export completion to free up memory,
	 */
	public purge(): void {
		this._tiles = [];
		this._origin = new Point(0, 0);
		this._expectedWidth = 0;
		this._expectedHeight = 0;
	}
	/**
	 * Converts every ICapturedTile into a blob and automatically downloads it as a bitmap image
	 */
	public downloadAll(): void {
		this._tiles.forEach((tile) => {
			convertCanvasToBlobAndDownload(tile.canvas).pipe(take(1)).subscribe();
		});
	}

	/**
	 * Converts the ICapturedTile at the provided index to a blob and automatically downlods it as a bitmap image
	 * @param index Index of the capture to download
	 */
	public downloadAtIndex(index: number): void {
		if (this._tiles[index] === undefined) {
			return;
		}

		convertCanvasToBlobAndDownload(this._tiles[index].canvas).pipe(take(1)).subscribe();
	}
	/**
	 * Capture the current rendered content as a tile.
	 * This method creates a render texture, renders the specified display object
	 * to that texture, and converts it to an HTML canvas element for export use.
	 * @param tileArea - The tile dimensions and position information
	 * @param root - The root DisplayObject to render (defaults to application.stage)
	 * @returns A captured tile containing the canvas and position data
	 * @private
	 */
	private captureTile(
		area: Rectangle,
		root: DisplayObject = this.application.stage,
		offset?: ITileOffset
	): ICapturedTile {
		const texture: RenderTexture = this.createRenderTexture(area.width, area.height);
		this.drawToTexture(root, texture);

		const offsetX: number = offset?.x || 0;
		const offsetY: number = offset?.y || 0;

		// If this is the first tile, we store the values as our origin. Every subsequent capture will then
		// be relative of this origin point.
		if (this._tiles.length === 0) {
			this._origin.x = area.x;
			this._origin.y = area.y;
			this._expectedWidth = area.width;
			this._expectedHeight = area.height;
		} else {
			// For subsequent captures, calculate the new width and height of all tiles in the array.
			const relativeX: number = area.x - this._origin.x + offsetX;
			const relativeY: number = area.y - this._origin.y + offsetY;

			const tileRightEdge: number = relativeX + area.width;
			if (tileRightEdge > this._expectedWidth) {
				this._expectedWidth = tileRightEdge;
			}

			const tileBottomEdge: number = relativeY + area.height;
			if (tileBottomEdge > this._expectedHeight) {
				this._expectedHeight = tileBottomEdge;
			}
		}

		const position: ITilePosition = {
			x: Math.abs(area.x - this._origin.x) + offsetX,
			y: Math.abs(area.y - this._origin.y) + offsetY,
			width: area.width,
			height: area.height,
		};

		return {
			canvas: this.convertTextureToCanvas(texture),
			position: position,
			index: this._tiles.length - 1,
		};
	}
	/**
	 * Create a new RenderTexture
	 * @param width - Width of the render texture in pixels
	 * @param height - Height of the render texture in pixels
	 * @returns A new RenderTexture ready for rendering
	 * @private
	 */
	private createRenderTexture(width: number, height: number): RenderTexture {
		return RenderTexture.create({ width, height });
	}
	/**
	 * Render a DisplayObject tree to a RenderTexture. The rootNode represents the object we will be drawing to the
	 * RenderTexture, including all children of that object. Typically we provide the scene graph root (Application.stage), which will then
	 * render the entire canvas view for that specific AbstractApplication.
	 * @param rootNode - The root DisplayObject to render (will render this node and all children)
	 * @param texture - The RenderTexture to draw the content into
	 * @private
	 */
	private drawToTexture(rootNode: DisplayObject, texture: RenderTexture): void {
		// For posterity, the reason why we draw to a RenderTexture first is because WebGL/WebGPU APIs do not
		// have an easy way to export their data into a HTMLCanvasElement. So we need to first render to a render texture
		// and then we can extract the pixel data from the RenderTexture and add it to a HTMLCanvasElement.
		// Then once we have a HTMLCanvasElement, it is trivial to convert that into a bitmap/image for download.
		// This step can technically be skipped if the renderer is a CanvasRenderer, however it adds some needless complexity.
		this.application.renderer.render(rootNode, { renderTexture: texture });
	}
	/**
	 * Convert a RenderTexture to an HTML canvas element. This method extracts the pixel data from the Pixi.js RenderTexture
	 * and creates an HTML canvas element containing the same image data.
	 * @param texture - The RenderTexture containing the rendered plan content
	 * @returns An HTML canvas element with the extracted image data
	 * @private
	 */
	private convertTextureToCanvas(texture: RenderTexture): HTMLCanvasElement {
		return this.application.renderer.extract.canvas(texture) as HTMLCanvasElement;
	}
}
